<template>
  <ModalComponent
    ref="modalRef"
    :submit-button-text="$t('common.close')"
    :dismissible="selectedEvent === null"
    size="xl"
    @close="$emit('close')"
  >
    <template #title>
      {{ group.typeLabel }}
    </template>

    <template #body>
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body p-3 p-lg-4">
          <h6 class="text-uppercase text-muted mb-2 fw-semibold section-label">
            {{ $t("company_orders.transactions.event_group") }}
          </h6>
          <div class="mb-3">
            <span class="badge badge-phoenix badge-phoenix-primary">
              {{ formatEventType(group.eventType, t) }}
            </span>
          </div>
          <div class="row g-2 g-lg-3">
            <div
              v-for="field in fields"
              :key="field.label"
              class="col-md-6 col-xl-3"
            >
              <div class="detail-item h-100">
                <div class="text-muted small mb-1">{{ field.label }}</div>
                <div class="fw-semibold text-break">{{ field.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 shadow-sm">
        <div class="card-body p-3 p-lg-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6
              class="text-uppercase text-muted mb-0 fw-semibold section-label"
            >
              {{ $t("company_orders.transactions.events") }}
            </h6>
            <span class="small text-muted">
              {{
                $t("company_orders.transactions.showing", {
                  shown: shownEvents.length,
                  total: group.events.length,
                })
              }}
            </span>
          </div>
          <div class="table-responsive">
            <table class="table table-sm mb-0 align-middle">
              <thead>
                <tr>
                  <th>{{ $t("company_orders.transactions.occurred") }}</th>
                  <th>
                    {{ $t("company_orders.transactions.registry_transaction") }}
                  </th>
                  <th class="text-end text-nowrap">MWh</th>
                  <th>{{ $t("company_orders.transactions.technology") }}</th>
                  <th>
                    {{ $t("company_orders.transactions.production_month") }}
                  </th>
                  <th>
                    {{ $t("company_orders.transactions.production_device") }}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="event in shownEvents" :key="event.id">
                  <td class="text-nowrap">
                    {{ formatDateTime(event.occurred_at) }}
                  </td>
                  <td>
                    <code>{{ event.registry_event_id }}</code>
                  </td>
                  <td class="text-end text-nowrap fw-semibold">
                    {{ formatMwh(event.linked_volume) }}
                  </td>
                  <td class="text-muted">
                    {{ formatTechnologies(event.technologies) }}
                  </td>
                  <td class="text-muted">
                    {{ event.production_months.join(", ") }}
                  </td>
                  <td class="text-muted text-nowrap">
                    {{ formatDevices(event.production_devices) }}
                  </td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      @click="selectedEvent = event"
                    >
                      {{ $t("company_orders.view") }}
                    </button>
                  </td>
                </tr>
                <tr class="fw-semibold">
                  <td colspan="2">
                    {{ $t("company_orders.transactions.total") }}
                  </td>
                  <td class="text-end text-nowrap">
                    {{ formatMwh(group.volume) }}
                  </td>
                  <td colspan="4"></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="shownEvents.length < group.events.length"
            class="d-flex justify-content-center mt-2"
          >
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="shownCount += PAGE_SIZE"
            >
              {{ $t("company_orders.transactions.load_more") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>

  <RegistryEventModal
    v-if="selectedEvent"
    :event="selectedEvent"
    @close="selectedEvent = null"
  />
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatDateTime, formatMwh } from "@/composables/formatUtils";
import { translateTechnology } from "@/utils/technologies";
import { sumBy } from "lodash-es";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import RegistryEventModal from "./RegistryEventModal.vue";
import type { RegistryEventSummary } from "./transactionLogTypes";
import type { EventGroupRow } from "./transactionRows";
import {
  formatConnection,
  formatEventType,
} from "./transactionTypePresentation";

const PAGE_SIZE = 25;

const props = defineProps<{
  group: EventGroupRow;
}>();

defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");

const shownCount = ref(PAGE_SIZE);
const selectedEvent = ref<RegistryEventSummary | null>(null);

const shownEvents = computed(() =>
  props.group.events.slice(0, shownCount.value)
);

const fields = computed(() => {
  const group = props.group;
  const from = formatDateTime(group.at);
  const to = formatDateTime(group.endedAt);
  const timeFields =
    from === to
      ? [{ label: t("company_orders.transactions.occurred"), value: from }]
      : [
          { label: t("company_orders.transactions.from"), value: from },
          { label: t("company_orders.transactions.to"), value: to },
        ];
  return [
    {
      label: t("company_orders.transactions.event_count"),
      value: String(group.events.length),
    },
    ...timeFields,
    { label: t("common.company"), value: group.companyName },
    ...(group.portfolioName
      ? [{ label: t("company_orders.portfolio"), value: group.portfolioName }]
      : []),
    {
      label: t("company_orders.transactions.volume"),
      value: `${formatMwh(group.volume)} MWh`,
    },
    {
      label: t("company_orders.transactions.certificates"),
      value: String(sumBy(group.events, "linked_certificate_count")),
    },
    {
      label: t("company_orders.transactions.connection"),
      value: formatConnection(group.events[0].connection, t),
    },
  ];
});

onMounted(() => {
  if (modalRef.value) modalRef.value.showModal = true;
});

const formatTechnologies = (technologies: string[]): string =>
  technologies
    .map((technology) => translateTechnology(technology, t))
    .join(", ");

const formatDevices = (devices: string[]): string =>
  devices.length > 1
    ? t("company_orders.transactions.production_device_count", {
        count: devices.length,
      })
    : devices.join(", ");
</script>

<style scoped lang="scss">
@use "./order-details-shared";
</style>
